'use client'

/**
 * CurrencyInput — controlled USD input that mirrors the Anthropic credit-balance
 * design: a `$` prefix glued to the left edge of the field, value typed as
 * dollars and converted to cents externally.
 *
 * The component is dollar-string–native internally to avoid the off-by-one
 * cents bugs you get if you round on every keystroke. We emit cents on each
 * change so consumers don't have to do the math themselves.
 *
 * Stephen 2026-05-22 — credit balance pivot.
 */

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  id?: string
  valueCents: number
  onChangeCents: (cents: number) => void
  /** Inclusive lower bound (cents). Default 0. */
  minCents?: number
  /** Inclusive upper bound (cents). Default no cap. */
  maxCents?: number
  disabled?: boolean
  error?: string
  /** Optional placeholder shown when the input is empty. */
  placeholder?: string
}

function centsToDisplay(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return ''
  // Whole-dollar amounts: drop the .00. Otherwise show 2 decimals.
  const dollars = cents / 100
  return dollars % 1 === 0 ? `${dollars}` : dollars.toFixed(2)
}

function displayToCents(display: string): number {
  const cleaned = display.replace(/[^0-9.]/g, '')
  const parsed = parseFloat(cleaned)
  if (!Number.isFinite(parsed) || parsed < 0) return 0
  return Math.round(parsed * 100)
}

export function CurrencyInput({
  id,
  valueCents,
  onChangeCents,
  minCents,
  maxCents,
  disabled,
  error,
  placeholder = '0',
}: Props) {
  const [display, setDisplay] = useState(() => centsToDisplay(valueCents))

  // Re-sync from props when consumer resets the form.
  useEffect(() => {
    const expected = centsToDisplay(valueCents)
    if (displayToCents(display) !== valueCents) {
      setDisplay(expected)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueCents])

  return (
    <div className="relative flex items-center">
      <span className="pointer-events-none absolute left-3 text-atrium-muted">$</span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        onChange={(e) => {
          const next = e.target.value
          // Allow empty or numeric-ish (one dot, digits)
          if (next === '' || /^[0-9]*\.?[0-9]{0,2}$/.test(next)) {
            setDisplay(next)
            const cents = displayToCents(next)
            // Don't clamp on every keystroke (user might be mid-edit) —
            // emit raw cents and let the consumer validate via min/max.
            onChangeCents(cents)
          }
        }}
        onBlur={() => {
          let cents = displayToCents(display)
          if (typeof minCents === 'number' && cents < minCents) cents = minCents
          if (typeof maxCents === 'number' && cents > maxCents) cents = maxCents
          setDisplay(centsToDisplay(cents))
          onChangeCents(cents)
        }}
        className={cn(
          'flex h-10 w-full items-center rounded-lg border bg-white pl-7 pr-3 text-sm text-atrium-black',
          // text-atrium-black auto-inverts to white in dark mode via CSS var (globals.css ~line 476).
          'dark:bg-atrium-inner',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-0 disabled:opacity-60',
          error ? 'border-red-400' : 'border-atrium-border',
        )}
      />
    </div>
  )
}
